import { PropTypes as MobxPropTypes, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DropdownItem } from 'reactstrap';

import globalTranslations from '../../../i18n/globalTranslations';
import Language from '../../../models/Language';
import { modelOf } from '../../../prop-types';
import { countryCodeFromLocale } from '../../../util/languageMapper';
import DropdownSelect from '../DropdownSelect';
import Icon from '../Icon';
import LocaleFlagIcon from '../LocaleFlagIcon';

const COMPACT_TRIGGER_EXTRA_FLAGS = 2;

const RegionalSettingsLanguageDropdown = ({
  language,
  languages,
  onSelect,
  showLabelText,
  showCompactFlagTrigger,
  toggleClassName,
  toggleNav,
  disabled,
  positionFixed,
  menuContainer,
  menuClassName,
}) => {
  const compactTriggerFlags = (() => {
    if (!showCompactFlagTrigger) {
      return [language];
    }

    const extras = languages
      .filter((languageOption) => languageOption.code !== language.code)
      .slice(0, COMPACT_TRIGGER_EXTRA_FLAGS);

    return [language, ...extras];
  })();

  const selectedLanguageLabel = showCompactFlagTrigger ? (
    <span className="RegionalSettingsLanguageDropdown__selected-item RegionalSettingsLanguageDropdown__selected-item--compact-flags">
      {compactTriggerFlags.map((languageOption) => (
        <LocaleFlagIcon
          key={languageOption.code}
          locale={countryCodeFromLocale(languageOption.code)}
          decorative
        />
      ))}
      <span
        className="RegionalSettingsLanguageDropdown__chevron"
        aria-hidden="true"
      >
        <Icon name="angle-down" />
      </span>
      <span className="sr-only">
        <FormattedMessage
          {...globalTranslations.languageLabel}
          values={{ language: language.name }}
        />
      </span>
    </span>
  ) : (
    <span className="RegionalSettingsLanguageDropdown__selected-item">
      <LocaleFlagIcon
        locale={countryCodeFromLocale(language.code)}
        decorative
      />
      <span className="RegionalSettingsLanguageDropdown__selected-item-text">
        {language.name}
      </span>
    </span>
  );

  return (
    <DropdownSelect
      className="RegionalSettingsLanguageDropdown"
      focusSelectedItemOnKeyboardOpen
      focusToggleOnClose
      toggleClassName={toggleClassName}
      toggleNav={toggleNav}
      caret={!showCompactFlagTrigger}
      compactLabel={showCompactFlagTrigger}
      disabled={disabled}
      positionFixed={positionFixed}
      menuContainer={menuContainer}
      menuClassName={menuClassName}
      label={
        showLabelText ? (
          <FormattedMessage
            {...globalTranslations.languageLabel}
            values={{
              language: selectedLanguageLabel,
            }}
          />
        ) : (
          selectedLanguageLabel
        )
      }
    >
      {languages.map((languageOption) => {
        const isActive = language.code === languageOption.code;

        return (
          <DropdownItem
            key={languageOption.code}
            className="RegionalSettingsLanguageDropdown__item"
            onClick={() => onSelect(languageOption)}
            active={isActive}
            aria-current={isActive ? 'true' : undefined}
          >
            <span>
              <LocaleFlagIcon
                locale={countryCodeFromLocale(languageOption.code)}
                decorative
              />
              {languageOption.name}
            </span>
          </DropdownItem>
        );
      })}
    </DropdownSelect>
  );
};

RegionalSettingsLanguageDropdown.propTypes = {
  language: modelOf(Language).isRequired,
  languages: MobxPropTypes.observableArrayOf(modelOf(Language)).isRequired,
  onSelect: PropTypes.func.isRequired,
  showLabelText: PropTypes.bool,
  showCompactFlagTrigger: PropTypes.bool,
  toggleClassName: PropTypes.string,
  toggleNav: PropTypes.bool,
  disabled: PropTypes.bool,
  positionFixed: PropTypes.bool,
  menuContainer: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.func,
  ]),
  menuClassName: PropTypes.string,
};

RegionalSettingsLanguageDropdown.defaultProps = {
  showLabelText: true,
  showCompactFlagTrigger: false,
  toggleClassName: undefined,
  toggleNav: false,
  disabled: false,
  positionFixed: false,
  menuContainer: undefined,
  menuClassName: undefined,
};

export default observer(RegionalSettingsLanguageDropdown);
